<template>
  <div class="facilities-page">
    <!-- 顶部横幅 -->
    <div class="facilities-banner">
      <div class="banner-content">
        <h1>校园设施</h1>
        <p>现代化的学习与创作环境</p>
      </div>
    </div>

    <!-- 面包屑导航 -->
    <div class="breadcrumb">
      <div class="container">
        <router-link to="/">首页</router-link>
        <span> / </span>
        <span>校园设施</span>
      </div>
    </div>

    <div class="container">
      <!-- 设施介绍 -->
      <section class="facilities-intro">
        <div class="intro-content">
          <div class="intro-text">
            <h2>我们的校园设施</h2>
            <p>杉原美校拥有现代化的教学设施和艺术创作空间，为学生提供一流的学习环境和创作条件。我们的设施不仅满足传统艺术教学需求，还配备了先进的数字艺术设备，为学生提供全方位的艺术体验。</p>
            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-number">50+</div>
                <div class="stat-label">专业教室</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">10+</div>
                <div class="stat-label">专业实验室</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">2</div>
                <div class="stat-label">美术馆</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">1</div>
                <div class="stat-label">图书馆</div>
              </div>
            </div>
          </div>
          <div class="intro-image">
            <img src="@/assets/images/facilities-overview.webp" alt="校园设施概览">
          </div>
        </div>
      </section>

      <!-- 设施分类展示 -->
      <section class="facilities-categories">
        <h2 class="section-title">设施分类</h2>
        <div class="categories-grid">
          <div
              v-for="(category, index) in categories"
              :key="index"
              class="category-card"
              @click="scrollToSection(category.id)"
          >
            <div class="category-icon">
              <i :class="category.icon"></i>
            </div>
            <h3>{{ category.name }}</h3>
          </div>
        </div>
      </section>

      <!-- 教学设施 -->
      <section id="teaching" class="facility-section">
        <h2 class="section-title">教学设施</h2>
        <div class="facility-gallery">
          <div v-for="(facility, index) in teachingFacilities" :key="index" class="facility-item">
            <div class="facility-image">
              <img :src="facility.image" :alt="facility.name">
            </div>
            <div class="facility-info">
              <h3>{{ facility.name }}</h3>
              <p>{{ facility.description }}</p>
              <div class="facility-specs" v-if="facility.specs">
                <div v-for="(spec, i) in facility.specs" :key="i" class="spec-item">
                  <i :class="spec.icon"></i>
                  <span>{{ spec.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 实验室设施 -->
      <section id="labs" class="facility-section">
        <h2 class="section-title">实验室设施</h2>
        <div class="facility-gallery">
          <div v-for="(facility, index) in labFacilities" :key="index" class="facility-item">
            <div class="facility-image">
              <img :src="facility.image" :alt="facility.name">
            </div>
            <div class="facility-info">
              <h3>{{ facility.name }}</h3>
              <p>{{ facility.description }}</p>
              <div class="facility-specs" v-if="facility.specs">
                <div v-for="(spec, i) in facility.specs" :key="i" class="spec-item">
                  <i :class="spec.icon"></i>
                  <span>{{ spec.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 公共设施 -->
      <section id="public" class="facility-section">
        <h2 class="section-title">公共设施</h2>
        <div class="facility-gallery">
          <div v-for="(facility, index) in publicFacilities" :key="index" class="facility-item">
            <div class="facility-image">
              <img :src="facility.image" :alt="facility.name">
            </div>
            <div class="facility-info">
              <h3>{{ facility.name }}</h3>
              <p>{{ facility.description }}</p>
              <div class="facility-specs" v-if="facility.specs">
                <div v-for="(spec, i) in facility.specs" :key="i" class="spec-item">
                  <i :class="spec.icon"></i>
                  <span>{{ spec.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FacilitiesPage',
  data() {
    return {
      categories: [
        { id: 'teaching', name: '教学设施', icon: 'fas fa-chalkboard-teacher' },
        { id: 'labs', name: '实验室', icon: 'fas fa-flask' },
        { id: 'public', name: '公共设施', icon: 'fas fa-building' }
      ],
      teachingFacilities: [
        {
          name: '专业画室',
          description: '宽敞明亮的专业画室，配备可调节画架、专业照明系统和静物台，为传统绘画教学提供理想环境。',
          image: require('@/assets/images/facilities/studio.webp'),
          specs: [
            { icon: 'fas fa-ruler-combined', text: '面积: 80-120㎡' },
            { icon: 'fas fa-user-friends', text: '容量: 20-30人' },
            { icon: 'fas fa-lightbulb', text: '专业照明系统' }
          ]
        },
        {
          name: '多媒体教室',
          description: '配备高清投影、互动白板和音响系统的多媒体教室，支持数字化艺术教学和演示。',
          image: require('@/assets/images/facilities/multimedia.webp'),
          specs: [
            { icon: 'fas fa-tv', text: '4K高清投影' },
            { icon: 'fas fa-microphone', text: '专业音响系统' },
            { icon: 'fas fa-laptop', text: '电脑工作站' }
          ]
        },
        {
          name: '雕塑工作室',
          description: '专业的雕塑创作空间，配备泥塑台、转台、雕刻工具和各种材料加工设备。',
          image: require('@/assets/images/facilities/sculpture.webp'),
          specs: [
            { icon: 'fas fa-weight', text: '承重地板' },
            { icon: 'fas fa-fan', text: '通风系统' },
            { icon: 'fas fa-tools', text: '专业工具' }
          ]
        }
      ],
      labFacilities: [
        {
          name: '数字媒体实验室',
          description: '配备高性能电脑、数位板和专业软件的实验室，支持数字绘画、3D建模和动画制作。',
          image: require('@/assets/images/facilities/digital-lab.webp'),
          specs: [
            { icon: 'fas fa-desktop', text: 'iMac工作站' },
            { icon: 'fas fa-tablet-alt', text: 'Wacom数位板' },
            { icon: 'fas fa-cubes', text: '3D打印机' }
          ]
        },
        {
          name: '摄影棚',
          description: '专业级摄影棚，配备各种灯光设备、背景布和道具，支持商业摄影和人像摄影教学。',
          image: require('@/assets/images/facilities/photo-studio.webp'),
          specs: [
            { icon: 'fas fa-camera', text: '专业相机' },
            { icon: 'fas fa-lightbulb', text: '影室灯系统' },
            { icon: 'fas fa-vector-square', text: '绿幕区' }
          ]
        },
        {
          name: '版画工作室',
          description: '专业的版画制作空间，配备铜版画、木刻、丝网印刷等各种版画制作设备。',
          image: require('@/assets/images/facilities/printmaking.webp'),
          specs: [
            { icon: 'fas fa-print', text: '版画印刷机' },
            { icon: 'fas fa-cut', text: '雕刻工具' },
            { icon: 'fas fa-burn', text: '蚀刻设备' }
          ]
        }
      ],
      publicFacilities: [
        {
          name: '学校图书馆',
          description: '收藏丰富的艺术类图书、期刊和数字资源，提供安静的学习环境和研究空间。',
          image: require('@/assets/images/facilities/library.webp'),
          specs: [
            { icon: 'fas fa-book', text: '10万+藏书' },
            { icon: 'fas fa-headphones', text: '多媒体区' },
            { icon: 'fas fa-wifi', text: '全馆WiFi' }
          ]
        },
        {
          name: '学生美术馆',
          description: '展示学生作品的专业展览空间，定期举办各种主题展览和毕业作品展。',
          image: require('@/assets/images/facilities/gallery.webp'),
          specs: [
            { icon: 'fas fa-expand', text: '800㎡展厅' },
            { icon: 'fas fa-lightbulb', text: '专业灯光' },
            { icon: 'fas fa-calendar-alt', text: '定期展览' }
          ]
        },
        {
          name: '学生活动中心',
          description: '多功能活动空间，配备休闲区、讨论室和表演场地，丰富学生课余生活。',
          image: require('@/assets/images/facilities/activity-center.webp'),
          specs: [
            { icon: 'fas fa-coffee', text: '休闲咖啡区' },
            { icon: 'fas fa-microphone-alt', text: '表演舞台' },
            { icon: 'fas fa-chess', text: '娱乐设施' }
          ]
        }
      ]
    }
  },
  methods: {
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
}
</script>

<style scoped>
.facilities-page {
  color: #333;
}

/* 顶部横幅样式 */
.facilities-banner {
  height: 400px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
  url('@/assets/images/common-banner.jpg');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  margin-bottom: 40px;
}

.banner-content h1 {
  font-size: 3rem;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.banner-content p {
  font-size: 1.5rem;
  opacity: 0.9;
}

/* 面包屑导航 */
.breadcrumb {
  padding: 15px 0;
  background-color: #f8f9fa;
  margin-bottom: 40px;
}

.breadcrumb a {
  color: var(--default-red-color);
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.breadcrumb span {
  color: #7f8c8d;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 设施介绍样式 */
.facilities-intro {
  margin-bottom: 60px;
}

.intro-content {
  display: flex;
  gap: 40px;
  align-items: center;
}

.intro-text {
  flex: 1;
}

.intro-text h2 {
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 20px;
}

.intro-text p {
  color: #555;
  line-height: 1.8;
  margin-bottom: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 20px;
  margin-top: 30px;
}

.stat-item {
  text-align: center;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.stat-number {
  font-size: 1.8rem;
  font-weight: bold;
  color: var(--default-red-color);
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  color: #7f8c8d;
}

.intro-image {
  flex: 1;
}

.intro-image img {
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

/* 设施分类样式 */
.facilities-categories {
  margin-bottom: 60px;
}

.section-title {
  text-align: center;
  font-size: 2rem;
  margin-bottom: 2rem;
  color: #2c3e50;
  position: relative;
  padding-bottom: 15px;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background-color: var(--default-red-color);
}

.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.category-card {
  background-color: white;
  padding: 30px 20px;
  border-radius: 8px;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.category-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  background-color: var(--default-red-color);
  color: white;
}

.category-card:hover .category-icon {
  background-color: white;
  color: var(--default-red-color);
}

.category-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 15px;
  background-color: var(--default-red-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.3s;
}

.category-card h3 {
  font-size: 1.2rem;
}

/* 设施展示区样式 */
.facility-section {
  margin-bottom: 80px;
}

.facility-gallery {
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
}

.facility-item {
  display: flex;
  gap: 30px;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.facility-image {
  flex: 1;
  min-height: 300px;
}

.facility-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.facility-info {
  flex: 1;
  padding: 30px;
}

.facility-info h3 {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 15px;
}

.facility-info p {
  color: #555;
  line-height: 1.8;
  margin-bottom: 20px;
}

.facility-specs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 25px;
}

.spec-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7f8c8d;
}

.spec-item i {
  color: var(--default-red-color);
}

/* 响应式设计 */
@media (max-width: 992px) {
  .intro-content {
    flex-direction: column;
  }

  .facility-item {
    flex-direction: column;
  }

  .facility-image {
    min-height: 200px;
  }
}

@media (max-width: 768px) {
  .facilities-banner {
    height: 300px;
  }

  .banner-content h1 {
    font-size: 2.5rem;
  }

  .categories-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .facilities-banner {
    height: 250px;
  }

  .banner-content h1 {
    font-size: 2rem;
  }

  .banner-content p {
    font-size: 1.2rem;
  }

  .categories-grid {
    grid-template-columns: 1fr;
  }

  .facility-specs {
    grid-template-columns: 1fr;
  }
}
</style>
